import { Plus, Trash2 } from "lucide-react";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Card } from "../ui";
import { useStrategy } from "./Strategy";
import { CATEGORIES, cellInput, daysSince, eur, KINDS, METHODS, NO_PM, rentalKeys, SortTh, STALE_DAYS, todayISO, type Asset, type Company, type LinkedRental } from "./shared";

// Sortable columns. Text sorts A→Z first, numbers and dates high→low first; clicking the same header flips the direction.
// `w` = share of the table width: the item name gets the room, Kind is narrow (its unit chips wrap).
const SORT_COLS = [
  { k: "name", label: "Item", dir: "asc", w: "23%" },
  { k: "category", label: "Category", dir: "asc", w: "10%" },
  { k: "location", label: "Where it sits", dir: "asc", w: "12%" },
  { k: "value", label: "Value (EUR)", dir: "desc", num: true, w: "9%" },
  { k: "income", label: "Pays / month", dir: "desc", num: true, w: "10%" },
  { k: "kind", label: "Kind", dir: "asc", w: "13%" },
  { k: "valuedOn", label: "Valued on", dir: "desc", w: "10%" },
  { k: "method", label: "How", dir: "asc", w: "10%" },
] as const;
type SortKey = (typeof SORT_COLS)[number]["k"];
const sortVal = (a: Asset, k: SortKey): number | string =>
  k === "value" || k === "income" ? Number(a[k]) || 0 : k === "category" ? CATEGORIES.findIndex((c) => c.k === a.category) : String(a[k] || "").toLowerCase();

export function AssetsTab({ assets, companies, update, addAsset }: { assets: Asset[]; companies: Company[]; update: (fn: (a: Asset[]) => Asset[]) => void; addAsset: (category?: string) => void }) {
  const strategy = useStrategy([]).data;
  const rentals = strategy?.rentals?.ok ? strategy.rentals : null;
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "value", dir: "desc" });
  // Row order is FROZEN between sorts: editing a value must not make the row jump away mid-typing. Re-sorted on a
  // header click, a filter change, or a row added/removed; a new row sits on top until the next sort.
  const [order, setOrder] = useState<string[]>([]);
  useEffect(() => {
    const sign = sort.dir === "asc" ? 1 : -1;
    const rows = [...assets].sort((a, b) => {
      const x = sortVal(a, sort.key), y = sortVal(b, sort.key);
      if (x < y) return -1 * sign;
      if (x > y) return 1 * sign;
      return String(a.name).localeCompare(String(b.name));
    });
    setOrder(rows.map((a) => a.id));
  }, [sort, filter, assets.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const clickSort = (col: (typeof SORT_COLS)[number]) => setSort((s) => (s.key === col.k ? { key: col.k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: col.k, dir: col.dir }));

  const patchAsset = (id: string, patch: Partial<Asset>) => update((xs) => xs.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const removeAsset = (a: Asset) => {
    if (!window.confirm(`Remove "${a.name || "this item"}" (${eur(a.value)}) from the list?`)) return;
    update((xs) => xs.filter((x) => x.id !== a.id));
  };
  const linkedOf = (a: Asset): LinkedRental | null => (a.rentalsKey && rentals ? rentals.linked.find((x) => x.id === a.id) ?? null : null);
  // What an asset pays per month: the rentals app's figure when it is linked there, otherwise the typed one
  const effIncome = (a: Asset) => { const l = linkedOf(a); return l && l.source === "feed" ? l.monthly : Number(a.income) || 0; };

  const rank = new Map(order.map((id, i) => [id, i]));
  const visible = assets.filter((a) => !filter || a.category === filter).sort((a, b) => (rank.get(a.id) ?? -1) - (rank.get(b.id) ?? -1));
  const sumValue = visible.reduce((s, a) => s + a.value, 0);
  const sumIncome = visible.reduce((s, a) => s + effIncome(a), 0);

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className="input !w-auto" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by category">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => addAsset(filter || "cash")}><Plus size={15} /> Add item</button>
        <span className="muted text-xs sm:ml-auto">Edit in place — saves by itself. Rows over a year old are tinted.</span>
      </div>
      <div className="overflow-x-auto">
        <table className="table" style={{ tableLayout: "fixed", minWidth: 1080 }}>
          <thead>
            <tr>
              {SORT_COLS.map((c) => <SortTh key={c.k} label={c.label} on={sort.key === c.k} dir={sort.dir} num={"num" in c} onClick={() => clickSort(c)} style={{ width: c.w }} />)}
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody className="[&_td]:!px-1.5 [&_td]:!py-1.5">
            {visible.map((a) => {
              const l = linkedOf(a);
              const feed = l && l.source === "feed" && rentalKeys(a).join("|") === l.keys.join("|") ? l : null;
              return (
                <tr key={a.id} className={clsx(daysSince(a.valuedOn) > STALE_DAYS && "bg-amber-50/70")}>
                  <td><input {...NO_PM} className={cellInput} value={a.name} placeholder="Name" title={a.name} onChange={(e) => patchAsset(a.id, { name: e.target.value })} /></td>
                  <td>
                    <select className={cellInput} value={a.category} onChange={(e) => patchAsset(a.id, { category: e.target.value })}>
                      {CATEGORIES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                    </select>
                  </td>
                  <td><input {...NO_PM} className={cellInput} value={a.location} placeholder={a.category === "property" ? "Town / use" : "Bank / company"} list="wl-locations" onChange={(e) => patchAsset(a.id, { location: e.target.value })} /></td>
                  <td><input className={clsx(cellInput, "text-right num")} type="number" step="1000" value={a.value} onChange={(e) => patchAsset(a.id, { value: Number(e.target.value) || 0 })} /></td>
                  <td className="text-right">
                    {feed ? (
                      <span
                        className="inline-flex flex-col items-end leading-tight num"
                        title={`From the rentals app, updated by itself, last 12 months ÷ 12. Short lets: ${eur(feed.net12)} net (${eur(feed.gross12)} gross), ${feed.priced} of ${feed.stays} stays have an amount, ${feed.nights} nights. Leases and events received: ${eur(feed.otherReceived12 || 0)}, still expected ${eur(feed.otherExpected || 0)}.`}
                      >
                        {eur(feed.monthly)}
                        <small className={clsx("text-[10.5px]", feed.complete ? "text-emerald-700" : "text-amber-700")}>
                          {feed.complete ? (feed.leaseLines || feed.events ? (feed.stays ? "rentals app" : "leases / events") : "rentals app") : `${feed.priced} of ${feed.stays} stays priced`}
                        </small>
                      </span>
                    ) : (
                      <input
                        className={clsx(cellInput, "text-right num")} type="number" step="100" min="0" value={a.income || ""} placeholder="0"
                        title={a.rentalsKey ? "Linked to the rentals app, which has no stays for it yet — this typed figure applies until the first stay appears there" : "What it pays you per month — rent, interest, dividend"}
                        onChange={(e) => patchAsset(a.id, { income: Number(e.target.value) || 0 })}
                      />
                    )}
                  </td>
                  <td>
                    {a.category === "investment" ? (
                      <select className={cellInput} value={a.kind || ""} onChange={(e) => patchAsset(a.id, { kind: e.target.value })}>
                        <option value="">kind…</option>
                        {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    ) : a.category === "property" && rentals ? (
                      <div className="flex flex-wrap items-center gap-1">
                        {rentalKeys(a).map((k) => (
                          <span key={k} className="chip !px-1.5 !text-[11px]" title={k}>
                            <span className="max-w-[90px] truncate">{k}</span>
                            <button aria-label={`Unlink ${k}`} className="muted hover:text-red-600" onClick={() => patchAsset(a.id, { rentalsKey: rentalKeys(a).filter((x) => x !== k).join(" | "), rentalsIds: "" })}>×</button>
                          </span>
                        ))}
                        <select
                          className={cellInput} value=""
                          title="Let short-term through the rentals app? Pick the unit and the income comes from there, updated by itself. Pick again to add a second unit."
                          onChange={(e) => { if (e.target.value) patchAsset(a.id, { rentalsKey: [...rentalKeys(a), e.target.value].join(" | "), rentalsIds: "" }); }}
                        >
                          <option value="">{rentalKeys(a).length ? "+ unit" : "own use / long let"}</option>
                          {rentals.properties.filter((p) => !rentalKeys(a).includes(p)).map((p) => <option key={p} value={p}>short let: {p}</option>)}
                        </select>
                      </div>
                    ) : (
                      <span className="muted pl-1.5">—</span>
                    )}
                  </td>
                  <td><input className={cellInput} type="date" value={a.valuedOn} onChange={(e) => patchAsset(a.id, { valuedOn: e.target.value || todayISO() })} /></td>
                  <td><input {...NO_PM} className={cellInput} value={a.method} list="wl-methods" placeholder="How valued" title={a.method} onChange={(e) => patchAsset(a.id, { method: e.target.value })} /></td>
                  <td className="text-center"><button className="btn btn-ghost btn-danger btn-sm !px-1.5" title="Remove" aria-label="Remove" onClick={() => removeAsset(a)}><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
            {!visible.length && <tr><td colSpan={9} className="muted !py-6 text-center">Nothing here yet — add an item.</td></tr>}
            <tr className="bg-[var(--panel-2)] font-semibold">
              <td colSpan={3} className="!px-3">{filter ? CATEGORIES.find((c) => c.k === filter)?.label : "Total"}</td>
              <td className="text-right num">{eur(sumValue)}</td>
              <td className="text-right num">{eur(sumIncome)}</td>
              <td colSpan={4} className="muted text-xs !font-normal">{visible.length} item{visible.length === 1 ? "" : "s"} · pays {eur(sumIncome * 12)} a year</td>
            </tr>
          </tbody>
        </table>
      </div>
      <datalist id="wl-methods">{METHODS.map((m) => <option key={m} value={m} />)}</datalist>
      <datalist id="wl-locations">{companies.map((c) => <option key={c.id || c.name} value={c.name} />)}</datalist>
    </Card>
  );
}
